import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import interactionPlugin, { DateClickArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import type { EventClickArg, DateSelectArg, EventDropArg, EventContentArg } from '@fullcalendar/core';
import { Plus, Minus, Settings, PanelLeftClose, PanelLeftOpen, Download, Loader2, Package, RotateCcw, Rss, Copy, RefreshCw, PartyPopper, Link2, Unlink, ChevronLeft, ChevronRight, List as ListIcon, GanttChart, CalendarDays, Briefcase, ListChecks, Palette, Check } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { GuiaModuloFlutuante } from '@/components/shared/GuiaModuloFlutuante';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as MiniCalendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EventoModal } from '@/components/calendario/EventoModal';
import { CalendarioModal } from '@/components/calendario/CalendarioModal';
import { CalendarioTimeline } from '@/components/calendario/CalendarioTimeline';
import { ItemVirtualPreviewDialog } from '@/components/calendario/ItemVirtualPreviewDialog';
import { Calendario, CalendarioEvento, CalendarioItemVirtual, Usuario } from '@/types';
import { calendarioService, calendarioEventoService, GoogleCalendarStatus } from '@/services/calendarioApi';
import { projetotService } from '@/services/projetotApi';
import { usuarioService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { exportAgendaPdf } from '@/lib/exportAgenda';
import {
  agruparCalendariosPorEmpresa,
  empresaIdDoCalendario,
  lerFiltroEmpresasSalvo,
  salvarFiltroEmpresas,
} from '@/lib/calendarioEmpresas';
import { getFeriadosBrasilIntervalo } from '@/lib/feriadosBrasil';
import './calendario.css';

interface FCEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  classNames?: string[];
  extendedProps: {
    evento: CalendarioEvento;
    /** Nome da empresa do calendário — só preenchido quando a pessoa tem mais de uma empresa na agenda. */
    empresaNome?: string;
  };
}

/**
 * Eventos "dia inteiro" são salvos como meia-noite UTC do dia (inclusive, no caso de
 * `data_fim`). O FullCalendar interpreta timestamps com timezone (`Z`) no fuso LOCAL do
 * navegador antes de posicionar o evento na grade — em fuso negativo (Brasil, UTC-3) isso
 * volta um dia. Para `allDay`, o FullCalendar espera uma string de data pura ("YYYY-MM-DD"),
 * igual ao que já é feito para feriados em `feriadosBrasil.ts`.
 */
function toFullCalendarDateOnly(iso: string, addDays = 0): string {
  const d = new Date(iso);
  if (addDays !== 0) d.setUTCDate(d.getUTCDate() + addDays);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * Caminho inverso de `toFullCalendarDateOnly`: ao arrastar/redimensionar um evento dia-inteiro,
 * `arg.event.endStr` vem no fim EXCLUSIVE do FullCalendar — precisa voltar 1 dia antes de virar
 * o `data_fim` INCLUSIVE que o backend espera (mesma convenção usada para exibir o evento).
 */
function dropResizeDataFim(event: { endStr: string; allDay: boolean }): string | undefined {
  if (!event.endStr) return undefined;
  return event.allDay ? toFullCalendarDateOnly(event.endStr, -1) : event.endStr;
}

// Cores predefinidas pra escolher no popover de cada Projeto/Atividade do ProjetoT (mesmo
// conjunto usado no cadastro de Calendário, `PRESET_COLORS` em CalendarioModal.tsx).
const PROJETOT_PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
];

function corPadraoItemVirtual(item: CalendarioItemVirtual): string {
  return item.tipo === 'projeto' ? '#6366F1' : '#0EA5E9';
}

/**
 * Projeto/Atividade do ProjetoT (evento virtual, nunca editável aqui) → formato de evento do
 * FullCalendar/Linha do tempo. Sempre dia-inteiro; se só uma das duas datas existir, vira um
 * evento de 1 dia só nessa data. `corCustom` é a cor escolhida pelo usuário pra esse item
 * específico (popover na sidebar) — sem ela, cai no padrão por tipo.
 */
function virtualToFCEvent(item: CalendarioItemVirtual, corCustom?: string, empresaNome?: string) {
  const inicio = item.data_inicio || item.data_fim!;
  const fimInclusive = item.data_fim || item.data_inicio!;
  const cor = corCustom || corPadraoItemVirtual(item);
  return {
    id: item.id,
    title: item.titulo,
    start: toFullCalendarDateOnly(inicio),
    end: toFullCalendarDateOnly(fimInclusive, 1),
    allDay: true,
    editable: false,
    backgroundColor: cor,
    borderColor: cor,
    classNames: [item.tipo === 'projeto' ? 'calendar-event--projeto' : 'calendar-event--atividade'],
    extendedProps: { isVirtual: true as const, virtual: item, empresaNome },
  };
}

const STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos os status' },
  { value: 'confirmado', label: 'Confirmados' },
  { value: 'pendente', label: 'Pendentes' },
  { value: 'cancelado', label: 'Cancelados' },
] as const;

const PRIORIDADE_OPTIONS = [
  { value: 'todas', label: 'Todas as prioridades' },
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
] as const;

const STATUS_LABELS: Record<'confirmado' | 'pendente' | 'cancelado', string> = {
  confirmado: 'Confirmado',
  pendente: 'Pendente',
  cancelado: 'Cancelado',
};

const PRIORIDADE_LABELS: Record<'baixa' | 'media' | 'alta', string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
};

type QuickSeriesAction = {
  tipo: 'move' | 'resize';
  evento: CalendarioEvento;
  data_inicio: string;
  data_fim?: string;
};

// Linha de Projeto/Atividade do ProjetoT na sidebar — mesmo padrão visual da lista de
// Calendários (checkbox com a cor do item + nome + botão de ferramenta que só aparece no
// hover), só que a "ferramenta" aqui é escolher a cor em vez de editar (não dá pra editar
// título/data por aqui — quem manda nisso é o ProjetoT).
function ItemProjetoTRow({
  item,
  ativo,
  onToggle,
  cor,
  onChangeCor,
}: {
  item: CalendarioItemVirtual;
  ativo: boolean;
  onToggle: () => void;
  cor: string;
  onChangeCor: (cor: string) => void;
}) {
  return (
    <div className="group flex items-center justify-between rounded-md px-1 py-1 hover:bg-muted/50">
      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
        <Checkbox
          checked={ativo}
          onCheckedChange={onToggle}
          className="h-4 w-4 shrink-0 rounded-sm border-2 data-[state=checked]:text-white"
          style={
            ativo
              ? ({ '--checkbox-bg': cor, backgroundColor: cor, borderColor: cor } as React.CSSProperties)
              : ({ borderColor: cor } as React.CSSProperties)
          }
        />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm" title={item.titulo}>
            {item.tipo === 'projeto' ? <Briefcase className="mr-1 inline h-3 w-3 text-muted-foreground" /> : <ListChecks className="mr-1 inline h-3 w-3 text-muted-foreground" />}
            {item.titulo}
          </span>
        </span>
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0"
            title="Escolher cor"
          >
            <Palette className="h-3.5 w-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56" align="end">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Cor no calendário</p>
          <div className="flex flex-wrap items-center gap-2">
            {PROJETOT_PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={cn(
                  'relative flex h-7 w-7 items-center justify-center rounded-full border-2 transition-all hover:scale-105',
                  cor === c ? 'ring-2 ring-offset-2' : 'border-white/70',
                )}
                style={{ backgroundColor: c, boxShadow: cor === c ? `0 0 0 2px ${c}` : undefined }}
                onClick={() => onChangeCor(c)}
                aria-label={`Cor ${c}`}
              >
                {cor === c && <Check className="h-3.5 w-3.5 text-white drop-shadow-sm" />}
              </button>
            ))}
            <Input
              type="color"
              value={cor}
              onChange={(e) => onChangeCor(e.target.value)}
              className="h-7 w-7 cursor-pointer p-0.5"
              title="Cor personalizada"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function CalendarioPage() {
  const { user, empresaAtiva, empresaFiltroId } = useAuth();
  const calendarRef = useRef<FullCalendar>(null);
  const currentRangeRef = useRef<{ start?: string; end?: string }>({});
  const requestCounterRef = useRef(0);

  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const isSuperAdmin = user?.tipo === 'superadmin';

  // Modo "grade" cobre Ano/Mês/Semana/Dia (zoom do próprio FullCalendar, trocado pelo seu
  // headerToolbar nativo) e "lista" troca a view do FullCalendar via API — só "timeline" sai do
  // FullCalendar de vez e renderiza o componente próprio de Linha do tempo.
  const [modoExibicao, setModoExibicao] = useState<'grade' | 'lista' | 'timeline'>('grade');
  const [fcViewType, setFcViewType] = useState('dayGridMonth');
  // Altura da célula do dia na grade mensal/anual — controla quantos eventos aparecem empilhados
  // antes de colapsar em "+N mais" (o FullCalendar recalcula isso sozinho a partir da altura
  // disponível, já que `dayMaxEvents` fica true; aqui só ajustamos essa altura via CSS).
  const ALTURAS_GRADE = ['compacta', 'padrao', 'confortavel', 'ampla'] as const;
  type AlturaGrade = (typeof ALTURAS_GRADE)[number];
  const [alturaGrade, setAlturaGrade] = useState<AlturaGrade>(() => {
    try {
      const stored = localStorage.getItem('calendario:alturaGrade') as AlturaGrade | null;
      if (stored && (ALTURAS_GRADE as readonly string[]).includes(stored)) return stored;
    } catch { /* ignore */ }
    return 'compacta';
  });
  const ALTURA_GRADE_LABEL: Record<AlturaGrade, string> = {
    compacta: 'Compacta',
    padrao: 'Padrão',
    confortavel: 'Confortável',
    ampla: 'Ampla',
  };
  const [fcTitle, setFcTitle] = useState('');
  // Mesmo cluster Ano/Mês/Semana/Dia serve pro zoom da grade (FullCalendar) e da Linha do tempo —
  // cada view do FullCalendar mapeia pra uma escala equivalente no componente de timeline.
  const [escalaTimeline, setEscalaTimeline] = useState<'ano' | 'mes' | 'semana' | 'dia'>('mes');
  const VIEW_PARA_ESCALA_TIMELINE: Record<string, 'ano' | 'mes' | 'semana' | 'dia'> = {
    multiMonthYear: 'ano',
    dayGridMonth: 'mes',
    timeGridWeek: 'semana',
    timeGridDay: 'dia',
  };

  const [calendarios, setCalendarios] = useState<Calendario[]>([]);
  const [eventosFiltrados, setEventosFiltrados] = useState<FCEvent[]>([]);
  const [ativados, setAtivados] = useState<Set<string>>(new Set());
  const [usuarios, setUsuarios] = useState<Pick<Usuario, 'id' | 'nome' | 'email'>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<'todos' | 'confirmado' | 'pendente' | 'cancelado'>('todos');
  const [prioridadeFiltro, setPrioridadeFiltro] = useState<'todas' | 'baixa' | 'media' | 'alta'>('todas');
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('todas');

  const effectiveEmpresaId = isSuperAdmin
    ? (empresaFiltroId || '')
    : empresaAtiva?.id || user?.empresa_id || '';

  // ─── Agenda única por pessoa ─────────────────────────────────────────────────
  // Fora o superadmin (que segue o filtro global de empresa), a agenda junta os calendários de
  // TODAS as empresas da pessoa que têm o módulo Calendário. Com mais de uma, aparece o filtro de
  // empresas (lembrado no navegador); trocar a empresa no topo não esconde as outras.
  const escopoPessoa = !isSuperAdmin;
  // Empresas da agenda vêm do servidor (mesma regra do backend: vínculo ativo, empresa ativa, módulo Calendário);
  // a lista de empresas da sessão pode trazer empresa inativa ou o vínculo legado de quem foi desligado.
  const [empresasAgenda, setEmpresasAgenda] = useState<Array<{ id: string; nome: string }>>([]);
  useEffect(() => {
    if (!escopoPessoa) {
      setEmpresasAgenda([]);
      return;
    }
    let ativo = true;
    calendarioService.getEmpresasDaAgenda().then((res) => {
      if (ativo && res.success) setEmpresasAgenda(res.data.map((e) => ({ id: e.empresa_id, nome: e.nome })));
    });
    return () => { ativo = false; };
  }, [escopoPessoa, user?.id]);
  const idsEmpresasAgenda = useMemo(() => empresasAgenda.map((empresa) => empresa.id), [empresasAgenda]);
  const nomesEmpresas = useMemo(
    () => Object.fromEntries(
      [...(user?.empresas ?? []), ...empresasAgenda].map((empresa) => [empresa.id, empresa.nome]),
    ),
    [user?.empresas, empresasAgenda],
  );
  const multiEmpresa = empresasAgenda.length > 1;
  // null = sem escolha salva = todas as empresas.
  const [empresasSalvas, setEmpresasSalvas] = useState<string[] | null>(() => lerFiltroEmpresasSalvo());
  const empresasFiltroIds = useMemo(() => {
    if (!multiEmpresa || !empresasSalvas) return idsEmpresasAgenda;
    const validas = empresasSalvas.filter((id) => idsEmpresasAgenda.includes(id));
    return validas.length > 0 ? validas : idsEmpresasAgenda;
  }, [multiEmpresa, empresasSalvas, idsEmpresasAgenda]);
  const filtroEmpresaAtivo = multiEmpresa && empresasFiltroIds.length < idsEmpresasAgenda.length;
  // Só manda `empresa_ids` quando o filtro restringe; sem ele o backend traz todas as empresas da pessoa.
  const empresaIdsParam = filtroEmpresaAtivo ? empresasFiltroIds : undefined;
  const chaveFiltroEmpresas = filtroEmpresaAtivo ? empresasFiltroIds.join(',') : 'todas';
  const paramsEscopo = useMemo(
    () => (escopoPessoa
      ? { escopo: 'pessoa' as const, ...(empresaIdsParam ? { empresaIds: empresaIdsParam } : {}) }
      : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [escopoPessoa, chaveFiltroEmpresas],
  );

  const nomesEmpresasFiltro = useMemo(
    () => empresasFiltroIds.map((id) => nomesEmpresas[id]).filter(Boolean).join(', '),
    [empresasFiltroIds, nomesEmpresas],
  );

  const alterarFiltroEmpresas = (proximo: string[]) => {
    const todas = proximo.length === 0 || proximo.length >= idsEmpresasAgenda.length;
    const valor = todas ? null : proximo;
    setEmpresasSalvas(valor);
    salvarFiltroEmpresas(valor);
  };
  const toggleEmpresaFiltro = (empresaId: string) => {
    // Com "Todas" ativo, clicar numa empresa isola essa empresa; depois os cliques somam/tiram.
    if (!filtroEmpresaAtivo) return alterarFiltroEmpresas([empresaId]);
    alterarFiltroEmpresas(
      empresasFiltroIds.includes(empresaId)
        ? empresasFiltroIds.filter((id) => id !== empresaId)
        : [...empresasFiltroIds, empresaId],
    );
  };

  // Controle de modais
  const [eventoModalOpen, setEventoModalOpen] = useState(false);
  // true quando o evento foi aberto via deep-link de outra tela (ex: Dias trabalhados/agenda do
  // perfil do usuário) — superadmin só visualiza, não edita nada de outra empresa/pessoa só de
  // estar auditando (decisão da Samantha, 28/09/2026).
  const [eventoSomenteLeitura, setEventoSomenteLeitura] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState<CalendarioEvento | null>(null);
  const [defaultStart, setDefaultStart] = useState<string | undefined>();
  const [defaultEnd, setDefaultEnd] = useState<string | undefined>();
  const [defaultCalendarioId, setDefaultCalendarioId] = useState<string | undefined>();

  const [calendarioModalOpen, setCalendarioModalOpen] = useState(false);
  const [calendarioSelecionado, setCalendarioSelecionado] = useState<Calendario | null>(null);
  const [quickSeriesDialogOpen, setQuickSeriesDialogOpen] = useState(false);
  const [quickSeriesLoading, setQuickSeriesLoading] = useState(false);
  const [pendingQuickSeriesAction, setPendingQuickSeriesAction] = useState<QuickSeriesAction | null>(null);
  const pendingQuickRevertRef = useRef<(() => void) | null>(null);

  const [miniCalendarMonth, setMiniCalendarMonth] = useState<Date>(new Date());

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('calendario:sidebarOpen');
      if (stored !== null) return stored !== 'false';
    } catch { /* ignore */ }
    return typeof window === 'undefined' || window.innerWidth >= 768;
  });

  // Largura da sidebar, ajustável arrastando a alça na borda direita (só quando aberta —
  // recolhida sempre tem 40px fixos, igual ao antigo `w-10`).
  const SIDEBAR_LARGURA_MIN = 180;
  const SIDEBAR_LARGURA_MAX = 480;
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem('calendario:sidebarWidth'));
      if (Number.isFinite(stored) && stored >= SIDEBAR_LARGURA_MIN && stored <= SIDEBAR_LARGURA_MAX) return stored;
    } catch { /* ignore */ }
    return 224; // equivalente ao antigo `w-56`
  });
  const [sidebarResizing, setSidebarResizing] = useState(false);

  const iniciarResizeSidebar = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    setSidebarResizing(true);

    const onMouseMove = (moveEvent: MouseEvent) => {
      const next = Math.min(SIDEBAR_LARGURA_MAX, Math.max(SIDEBAR_LARGURA_MIN, startWidth + (moveEvent.clientX - startX)));
      setSidebarWidth(next);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setSidebarResizing(false);
      setSidebarWidth((current) => {
        try { localStorage.setItem('calendario:sidebarWidth', String(current)); } catch { /* ignore */ }
        return current;
      });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const [feriadosAtivo, setFeriadosAtivo] = useState<boolean>(() => {
    try {
      return localStorage.getItem('calendario:feriadosAtivo') === 'true';
    } catch {
      return false;
    }
  });

  // ProjetoT (projetos e atividades com data) como eventos virtuais — mesma ideia dos feriados,
  // só que o dado vem do backend (não dá pra calcular no cliente) em vez de ser determinístico.
  // Ativação e cor são por item (não mais um toggle único pra "todos os projetos"), mesmo padrão
  // já usado pra `ativados`/`cor` de cada Calendário real.
  const [itensVirtuaisProjetoT, setItensVirtuaisProjetoT] = useState<CalendarioItemVirtual[]>([]);
  const [itensProjetoTAtivados, setItensProjetoTAtivados] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('calendario:itensProjetoTAtivados');
      if (stored) return new Set(JSON.parse(stored));
    } catch { /* ignore */ }
    return new Set();
  });
  const [coresItensProjetoT, setCoresItensProjetoT] = useState<Record<string, string>>(() => {
    try {
      const stored = localStorage.getItem('calendario:coresItensProjetoT');
      if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return {};
  });
  const [virtualPreviewItem, setVirtualPreviewItem] = useState<CalendarioItemVirtual | null>(null);
  const [virtualPreviewOpen, setVirtualPreviewOpen] = useState(false);
  const virtualRequestCounterRef = useRef(0);

  const toggleItemProjetoT = (id: string) => {
    setItensProjetoTAtivados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem('calendario:itensProjetoTAtivados', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const setCorItemProjetoT = (id: string, cor: string) => {
    setCoresItensProjetoT((prev) => {
      const next = { ...prev, [id]: cor };
      try { localStorage.setItem('calendario:coresItensProjetoT', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Checkbox mestre do cabeçalho "Projetos": liga/desliga todos os projetos e atividades de uma vez.
  const alternarTodosItensProjetoT = () => {
    setItensProjetoTAtivados((prev) => {
      const todosAtivos = itensVirtuaisProjetoT.length > 0 && itensVirtuaisProjetoT.every((i) => prev.has(i.id));
      const next = todosAtivos ? new Set<string>() : new Set(itensVirtuaisProjetoT.map((i) => i.id));
      try { localStorage.setItem('calendario:itensProjetoTAtivados', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormato, setExportFormato] = useState<'pdf' | 'ics'>('pdf');
  const [exporting, setExporting] = useState(false);

  const [assinaturaDialogOpen, setAssinaturaDialogOpen] = useState(false);
  const [assinaturaUrl, setAssinaturaUrl] = useState('');
  const [assinaturaLoading, setAssinaturaLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  const [googleStatus, setGoogleStatus] = useState<GoogleCalendarStatus | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleSelecionados, setGoogleSelecionados] = useState<Set<string>>(new Set());

  // `pode_editar` vem da API (agenda única: papel da pessoa na empresa do calendário). Sem ele
  // (superadmin), vale a regra antiga: admin edita tudo; comum, só com acesso de edição.
  const podeEditarCalendario = useCallback(
    (cal?: Calendario) => {
      if (!cal) return false;
      if (typeof cal.pode_editar === 'boolean') return cal.pode_editar;
      return isAdmin || !!cal.acessos?.some((acesso) => acesso.pode_editar);
    },
    [isAdmin],
  );
  // O filtro de empresas esconde da agenda (lista, eventos, exportação) as empresas desmarcadas.
  const calendariosVisiveis = useMemo(
    () => (multiEmpresa
      ? calendarios.filter((cal) => empresasFiltroIds.includes(empresaIdDoCalendario(cal)))
      : calendarios),
    [multiEmpresa, calendarios, empresasFiltroIds],
  );
  const idsAtivosVisiveis = useMemo(
    () => calendariosVisiveis.filter((cal) => ativados.has(cal.id)).map((cal) => cal.id),
    [calendariosVisiveis, ativados],
  );
  const gruposCalendarios = useMemo(
    () => (multiEmpresa ? agruparCalendariosPorEmpresa(calendariosVisiveis, nomesEmpresas, empresaAtiva?.id) : []),
    [multiEmpresa, calendariosVisiveis, nomesEmpresas, empresaAtiva?.id],
  );
  // Diálogo do Google: todos os calendários da pessoa, agrupados por empresa quando há mais de uma.
  const gruposCalendariosGoogle = useMemo(
    () => (multiEmpresa
      ? agruparCalendariosPorEmpresa(calendarios, nomesEmpresas, empresaAtiva?.id)
      : [{ empresaId: '', empresaNome: '', calendarios }]),
    [multiEmpresa, calendarios, nomesEmpresas, empresaAtiva?.id],
  );
  const empresasAdminIds = useMemo(
    () => (user?.empresas ?? []).filter((empresa) => empresa.tipo === 'admin').map((empresa) => empresa.id),
    [user?.empresas],
  );
  const calendariosEditaveis = calendariosVisiveis
    .filter((cal) => podeEditarCalendario(cal))
    // Calendário da empresa ativa primeiro: é o padrão ao criar um evento.
    .sort((a, b) => Number(empresaIdDoCalendario(b) === empresaAtiva?.id) - Number(empresaIdDoCalendario(a) === empresaAtiva?.id));
  const canCreateEvent = calendariosEditaveis.length > 0 && (!isSuperAdmin || !!effectiveEmpresaId);
  const categoriasDisponiveis = Array.from(
    new Set(
      eventosFiltrados
        .map(({ extendedProps }) => extendedProps.evento.categoria?.trim())
        .filter((categoria): categoria is string => !!categoria),
    ),
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const termoBusca = busca.trim().toLowerCase();
  const eventosVisiveis = eventosFiltrados.filter(({ extendedProps }) => {
    const evento = extendedProps.evento;

    if (statusFiltro !== 'todos' && evento.status !== statusFiltro) return false;
    if (prioridadeFiltro !== 'todas' && evento.prioridade !== prioridadeFiltro) return false;
    if (categoriaFiltro !== 'todas' && (evento.categoria || '') !== categoriaFiltro) return false;

    if (!termoBusca) return true;

    const haystack = [
      evento.titulo,
      evento.descricao,
      evento.local,
      evento.categoria,
      evento.calendario?.nome,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(termoBusca);
  });

  // Feriados nacionais: calculados no cliente, sem round-trip ao backend.
  // Janela fixa de alguns anos cobre a navegação típica do usuário no calendário.
  const feriadoEvents = useMemo(() => {
    if (!feriadosAtivo) return [];
    const anoBase = new Date().getFullYear();
    return getFeriadosBrasilIntervalo(anoBase - 1, anoBase + 2).map((feriado) => ({
      id: `feriado-${feriado.data}-${feriado.nome}`,
      title: feriado.nome,
      start: feriado.data,
      allDay: true,
      editable: false,
      backgroundColor: feriado.tipo === 'nacional' ? '#16A34A' : '#84CC16',
      borderColor: feriado.tipo === 'nacional' ? '#16A34A' : '#84CC16',
      classNames: ['calendar-event--feriado'],
      extendedProps: { isFeriado: true as const },
    }));
  }, [feriadosAtivo]);

  // Projetos e atividades do ProjetoT com data: eventos virtuais vindos do backend (não são
  // linhas de `calendario_eventos` — clicáveis, mas só abrem um preview read-only). Ativação e
  // cor são por item, igual a `ativados`/`cor` de cada Calendário real.
  const projetoEvents = useMemo(
    () => itensVirtuaisProjetoT
      .filter((i) => i.tipo === 'projeto' && itensProjetoTAtivados.has(i.id))
      .map((i) => virtualToFCEvent(i, coresItensProjetoT[i.id], multiEmpresa && i.empresa_id ? nomesEmpresas[i.empresa_id] : undefined)),
    [itensVirtuaisProjetoT, itensProjetoTAtivados, coresItensProjetoT, multiEmpresa, nomesEmpresas],
  );
  const atividadeEvents = useMemo(
    () => itensVirtuaisProjetoT
      .filter((i) => i.tipo === 'atividade' && itensProjetoTAtivados.has(i.id))
      .map((i) => virtualToFCEvent(i, coresItensProjetoT[i.id], multiEmpresa && i.empresa_id ? nomesEmpresas[i.empresa_id] : undefined)),
    [itensVirtuaisProjetoT, itensProjetoTAtivados, coresItensProjetoT, multiEmpresa, nomesEmpresas],
  );

  // Agrupa cada atividade sob o respectivo projeto na sidebar (em vez de duas listas soltas).
  // Uma atividade cujo projeto não tem data própria (por isso não aparece como item "projeto")
  // vira um grupo "órfão", com só o nome do projeto como cabeçalho (não clicável/sem check).
  const gruposProjetoT = useMemo(() => {
    const projetos = itensVirtuaisProjetoT.filter((i) => i.tipo === 'projeto');
    const atividades = itensVirtuaisProjetoT.filter((i) => i.tipo === 'atividade');

    const atividadesPorProjetoId = new Map<string, CalendarioItemVirtual[]>();
    atividades.forEach((a) => {
      const lista = atividadesPorProjetoId.get(a.projeto_id) || [];
      lista.push(a);
      atividadesPorProjetoId.set(a.projeto_id, lista);
    });
    atividadesPorProjetoId.forEach((lista) => lista.sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR')));

    const gruposComProjeto = projetos
      .slice()
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'))
      .map((projetoItem) => ({
        projetoItem,
        projetoTitulo: projetoItem.titulo,
        atividades: atividadesPorProjetoId.get(projetoItem.id) || [],
      }));

    const idsComItemProprio = new Set(projetos.map((p) => p.id));
    const gruposOrfaos = [...atividadesPorProjetoId.entries()]
      .filter(([projetoId]) => !idsComItemProprio.has(projetoId))
      .map(([, lista]) => ({
        projetoItem: null as CalendarioItemVirtual | null,
        projetoTitulo: lista[0]?.projeto_titulo || '',
        atividades: lista,
      }))
      .sort((a, b) => a.projetoTitulo.localeCompare(b.projetoTitulo, 'pt-BR'));

    return [...gruposComProjeto, ...gruposOrfaos];
  }, [itensVirtuaisProjetoT]);

  // ─── Carregar dados ────────────────────────────────────────────────────────

  const carregarCalendarios = useCallback(async () => {
    if (isSuperAdmin && !effectiveEmpresaId) {
      setCalendarios([]);
      setAtivados(new Set());
      return;
    }
    const res = await calendarioService.getAll(
      isSuperAdmin ? effectiveEmpresaId : undefined,
      escopoPessoa ? { escopo: 'pessoa' } : {},
    );
    if (res.success) {
      setCalendarios(res.data);
      const allIds = new Set(res.data.map(c => c.id));
      try {
        const storageKey = `calendario:ativados${isSuperAdmin ? `:${effectiveEmpresaId}` : ''}`;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const savedIds = new Set<string>(JSON.parse(saved) as string[]);
          const valid = new Set([...savedIds].filter(id => allIds.has(id)));
          setAtivados(valid.size > 0 ? valid : allIds);
        } else {
          setAtivados(allIds);
        }
      } catch {
        setAtivados(allIds);
      }
    }
  }, [isSuperAdmin, effectiveEmpresaId, escopoPessoa]);

  const carregarEventos = useCallback(
    async (start?: string, end?: string) => {
      if (idsAtivosVisiveis.length === 0) {
        setEventosFiltrados([]);
        return;
      }

      const requestId = ++requestCounterRef.current;

      const res = await calendarioEventoService.getEventos({
        start,
        end,
        calendarioIds: idsAtivosVisiveis,
        ...(isSuperAdmin && effectiveEmpresaId ? { empresa_id: effectiveEmpresaId } : {}),
        ...paramsEscopo,
      });

      if (requestId !== requestCounterRef.current) return;
      if (!res.success) return;

      const fcEvents: FCEvent[] = res.data.map(ev => {
        const cal = calendarios.find(c => c.id === ev.calendario_id);
        const cor = cal?.cor || ev.calendario?.cor || '#3B82F6';
        const isSeriesOrigin = !!ev.recorrencia_grupo_id;
        const exceptionType = ev.recorrencia_excecao_tipo;
        // `data_fim` de evento dia-inteiro é o último dia INCLUSIVE; o FullCalendar espera
        // `end` EXCLUSIVE para allDay, por isso soma 1 dia (mesmo ajuste feito para o Google
        // Calendar em `eventoParaGoogleInput`, no backend).
        const start = ev.dia_inteiro ? toFullCalendarDateOnly(ev.data_inicio) : ev.data_inicio;
        const end = ev.dia_inteiro
          ? (ev.data_fim ? toFullCalendarDateOnly(ev.data_fim, 1) : undefined)
          : (ev.data_fim || undefined);
        const empresaId = cal ? empresaIdDoCalendario(cal) : (ev.calendario?.empresa_id || '');
        const empresaNome = multiEmpresa ? (cal?.empresa?.nome || nomesEmpresas[empresaId] || undefined) : undefined;
        return {
          id: ev.id,
          title: ev.titulo,
          start,
          end,
          allDay: ev.dia_inteiro,
          backgroundColor: cor,
          borderColor: cor,
          classNames: [
            `calendar-event--status-${ev.status || 'confirmado'}`,
            `calendar-event--priority-${ev.prioridade || 'media'}`,
            isSeriesOrigin ? 'calendar-event--series-origin' : '',
            exceptionType ? `calendar-event--exception-${exceptionType}` : '',
          ],
          extendedProps: { evento: ev, empresaNome },
        };
      });

      setEventosFiltrados(fcEvents);
    },
    [idsAtivosVisiveis, calendarios, isSuperAdmin, effectiveEmpresaId, paramsEscopo, multiEmpresa, nomesEmpresas],
  );

  // Busca sempre (independente de qualquer item estar ativo) pra popular a lista da sidebar —
  // mesmo padrão de `carregarCalendarios`, que também não depende de `ativados`.
  const carregarItensVirtuaisProjetoT = useCallback(
    async (start?: string, end?: string) => {
      const requestId = ++virtualRequestCounterRef.current;

      const res = await projetotService.getCalendarioEventos({
        start,
        end,
        ...(isSuperAdmin && effectiveEmpresaId ? { empresa_id: effectiveEmpresaId } : {}),
        ...paramsEscopo,
      });

      if (requestId !== virtualRequestCounterRef.current) return;
      if (!res.success) return;

      // Defesa: se o filtro de empresas está ativo e o item traz `empresa_id`, descarta o de empresa
      // desmarcada (item sem `empresa_id` — backend antigo — passa direto).
      setItensVirtuaisProjetoT(
        filtroEmpresaAtivo
          ? res.data.filter((item) => !item.empresa_id || empresasFiltroIds.includes(item.empresa_id))
          : res.data,
      );
    },
    [isSuperAdmin, effectiveEmpresaId, paramsEscopo, filtroEmpresaAtivo, empresasFiltroIds],
  );

  // Recarrega calendários + usuários quando muda a empresa efetiva
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await carregarCalendarios();
      // Usuários (participantes/acessos) são os da empresa ativa; o modal busca os de outra empresa
      // quando o calendário escolhido é dela.
      if (isAdmin) {
        const res = isSuperAdmin && effectiveEmpresaId
          ? await usuarioService.getByEmpresa(effectiveEmpresaId)
          : await usuarioService.getAll();
        if (res.success) setUsuarios(res.data);
      } else {
        setUsuarios([]);
      }
      setIsLoading(false);
    };
    init();
  }, [carregarCalendarios, isAdmin, isSuperAdmin, effectiveEmpresaId]);

  useEffect(() => {
    if (!isLoading) {
      carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsAtivosVisiveis, calendarios, isLoading, chaveFiltroEmpresas]);

  // Mudou o filtro de empresas: recarrega também os projetos/atividades (os eventos já recarregam acima).
  useEffect(() => {
    if (isLoading || !currentRangeRef.current.start) return;
    carregarItensVirtuaisProjetoT(currentRangeRef.current.start, currentRangeRef.current.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveFiltroEmpresas]);

  // Atualização quase em tempo real: recarrega silenciosamente o período
  // visível a cada minuto, para refletir mudanças feitas por outros usuários.
  useEffect(() => {
    const interval = setInterval(() => {
      if (isLoading || !currentRangeRef.current.start) return;
      carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
    }, 60_000);
    return () => clearInterval(interval);
  }, [isLoading, carregarEventos]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const toggleCalendario = (id: string) => {
    setAtivados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try {
        const storageKey = `calendario:ativados${isSuperAdmin ? `:${effectiveEmpresaId}` : ''}`;
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };

  const handleDateClick = (arg: DateClickArg) => {
    if (!canCreateEvent) return;
    setEventoSelecionado(null);
    setDefaultStart(arg.dateStr);
    setDefaultEnd(undefined);
    setDefaultCalendarioId(calendariosEditaveis[0]?.id);
    setEventoModalOpen(true);
  };

  const handleSelect = (arg: DateSelectArg) => {
    if (!canCreateEvent) return;
    setEventoSelecionado(null);
    setDefaultStart(arg.startStr);
    setDefaultEnd(arg.endStr);
    setDefaultCalendarioId(calendariosEditaveis[0]?.id);
    setEventoModalOpen(true);
  };

  const abrirEvento = (evento: CalendarioEvento, somenteLeitura = false) => {
    setEventoSelecionado(evento);
    setDefaultStart(undefined);
    setDefaultEnd(undefined);
    setEventoSomenteLeitura(somenteLeitura);
    setEventoModalOpen(true);
  };

  const abrirPreviewVirtual = (item: CalendarioItemVirtual) => {
    setVirtualPreviewItem(item);
    setVirtualPreviewOpen(true);
  };

  const handleEventClick = (arg: EventClickArg) => {
    if (arg.event.extendedProps.isFeriado) return;
    if (arg.event.extendedProps.isVirtual) {
      abrirPreviewVirtual(arg.event.extendedProps.virtual as CalendarioItemVirtual);
      return;
    }
    abrirEvento(arg.event.extendedProps.evento as CalendarioEvento);
  };

  const handleEventDrop = async (arg: EventDropArg) => {
    const evento = arg.event.extendedProps.evento as CalendarioEvento;
    const calendario = calendarios.find((cal) => cal.id === evento.calendario_id);
    if (!podeEditarCalendario(calendario)) {
      arg.revert();
      toast.error('Você não tem permissão para editar este calendário');
      return;
    }
    if (evento.recorrencia_grupo_id) {
      pendingQuickRevertRef.current = () => arg.revert();
      setPendingQuickSeriesAction({
        tipo: 'move',
        evento,
        data_inicio: arg.event.startStr,
        data_fim: dropResizeDataFim(arg.event),
      });
      setQuickSeriesDialogOpen(true);
      return;
    }
    try {
      const res = await calendarioEventoService.update(evento.calendario_id, evento.id, {
        data_inicio: arg.event.startStr,
        data_fim: dropResizeDataFim(arg.event),
      });
      if (!res.success) {
        arg.revert();
        toast.error('Erro ao mover evento');
      }
    } catch {
      arg.revert();
      toast.error('Erro ao mover evento');
    }
  };

  const handleEventResize = async (arg: EventResizeDoneArg) => {
    const evento = arg.event.extendedProps.evento as CalendarioEvento;
    const calendario = calendarios.find((cal) => cal.id === evento.calendario_id);
    if (!podeEditarCalendario(calendario)) {
      arg.revert();
      toast.error('Você não tem permissão para editar este calendário');
      return;
    }
    if (evento.recorrencia_grupo_id) {
      pendingQuickRevertRef.current = () => arg.revert();
      setPendingQuickSeriesAction({
        tipo: 'resize',
        evento,
        data_inicio: arg.event.startStr,
        data_fim: dropResizeDataFim(arg.event),
      });
      setQuickSeriesDialogOpen(true);
      return;
    }
    try {
      const res = await calendarioEventoService.update(evento.calendario_id, evento.id, {
        data_inicio: arg.event.startStr,
        data_fim: dropResizeDataFim(arg.event),
      });
      if (!res.success) {
        arg.revert();
        toast.error('Erro ao redimensionar evento');
      }
    } catch {
      arg.revert();
      toast.error('Erro ao redimensionar evento');
    }
  };

  const handleDatesSet = (info: { startStr: string; endStr: string; view: { currentStart: Date; type: string; title: string } }) => {
    currentRangeRef.current = { start: info.startStr, end: info.endStr };
    carregarEventos(info.startStr, info.endStr);
    carregarItensVirtuaisProjetoT(info.startStr, info.endStr);
    // view.currentStart é o início do mês/período exibido de fato — start/end
    // incluem os dias de preenchimento do grid (podem cair no mês anterior)
    setMiniCalendarMonth(info.view.currentStart);
    // Sincroniza o cluster de zoom/modo externo (Ano/Mês/Semana/Dia/Lista) com a view real do
    // FullCalendar — cobre tanto os botões próprios quanto navegação por atalho/teclado.
    setFcViewType(info.view.type);
    setFcTitle(info.view.title);
    if (info.view.type === 'listWeek') setModoExibicao('lista');
    else if (info.view.type !== 'list') setModoExibicao((atual) => (atual === 'timeline' ? atual : 'grade'));
  };

  const handleNovoEvento = () => {
    if (!canCreateEvent) return;
    setEventoSelecionado(null);
    setDefaultStart(new Date().toISOString());
    setDefaultEnd(undefined);
    setDefaultCalendarioId(calendariosEditaveis[0]?.id);
    setEventoModalOpen(true);
  };

  const handleNovoCalendario = () => {
    setCalendarioSelecionado(null);
    setCalendarioModalOpen(true);
  };

  const handleEditarCalendario = (cal: Calendario) => {
    setCalendarioSelecionado(cal);
    setCalendarioModalOpen(true);
  };

  const handleOpenAssinatura = async () => {
    setAssinaturaDialogOpen(true);
    if (assinaturaUrl) return;
    setAssinaturaLoading(true);
    try {
      const res = await calendarioService.getFeedUrl();
      if (!res.success) throw new Error(res.error);
      setAssinaturaUrl(res.data.url);
    } catch {
      toast.error('Erro ao obter URL de assinatura');
    } finally {
      setAssinaturaLoading(false);
    }
  };

  const handleRegenerarAssinatura = async () => {
    setAssinaturaLoading(true);
    try {
      const res = await calendarioService.regenerateFeedUrl();
      if (!res.success) throw new Error(res.error);
      setAssinaturaUrl(res.data.url);
      toast.success('Nova URL gerada — a anterior parou de funcionar.');
    } catch {
      toast.error('Erro ao gerar nova URL de assinatura');
    } finally {
      setAssinaturaLoading(false);
    }
  };

  const handleCopiarAssinatura = async () => {
    try {
      await navigator.clipboard.writeText(assinaturaUrl);
      toast.success('URL copiada!');
    } catch {
      toast.error('Não foi possível copiar automaticamente — selecione e copie manualmente.');
    }
  };

  const carregarGoogleStatus = useCallback(async () => {
    setGoogleLoading(true);
    try {
      const res = await calendarioService.getGoogleStatus();
      if (!res.success) throw new Error(res.error);
      setGoogleStatus(res.data);
      setGoogleSelecionados(new Set(res.data.calendarios_sincronizados));
    } catch {
      toast.error('Erro ao obter status do Google Calendar');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  const handleOpenGoogleDialog = () => {
    setGoogleDialogOpen(true);
    carregarGoogleStatus();
  };

  const handleConectarGoogle = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const res = await calendarioService.getGoogleConnectUrl();
      if (!res.success) throw new Error(res.error);
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao iniciar conexão com o Google Calendar');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleDesconectarGoogle = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      const res = await calendarioService.disconnectGoogle();
      if (!res.success) throw new Error(res.error);
      toast.success('Desconectado do Google Calendar');
      await carregarGoogleStatus();
    } catch {
      toast.error('Erro ao desconectar do Google Calendar');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleToggleGoogleCalendario = (calendarioId: string, checked: boolean) => {
    setGoogleSelecionados((prev) => {
      const next = new Set(prev);
      if (checked) next.add(calendarioId);
      else next.delete(calendarioId);
      return next;
    });
  };

  const handleSalvarGoogleCalendarios = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    try {
      // Só reenvia IDs de calendários que o usuário ainda vê agora — evita reenviar um ID
      // "invisível" (acesso revogado, calendário excluído) que ficou preso no estado local
      // sem que o usuário tivesse como desmarcá-lo na lista de checkboxes.
      // Agenda única: a seleção enviada substitui a escolha INTEIRA, então vale a de todas as empresas
      // (não só as do filtro de empresas da tela).
      const idsVisiveis = new Set(calendarios.map((c) => c.id));
      const calendarioIds = Array.from(googleSelecionados).filter((id) => idsVisiveis.has(id));
      const res = await calendarioService.setGoogleSyncedCalendars(calendarioIds);
      if (!res.success) throw new Error(res.error);
      toast.success('Calendários sincronizados atualizados');
      await carregarGoogleStatus();
    } catch {
      toast.error('Erro ao salvar calendários sincronizados');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSincronizarGoogleAgora = async () => {
    if (googleSyncing) return;
    setGoogleSyncing(true);
    try {
      const res = await calendarioService.syncGoogleNow();
      if (!res.success) throw new Error(res.error);
      toast.success(`Sincronizado — ${res.data.enviados} enviado(s), ${res.data.recebidos} recebido(s)`);
      await carregarGoogleStatus();
      carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao sincronizar com o Google Calendar');
    } finally {
      setGoogleSyncing(false);
    }
  };

  // Deep-link vindo de outra tela (ex: agenda do perfil do usuário, ?data=&evento=): navega pro
  // dia certo; a abertura do evento em si espera eventosFiltrados carregar (efeito abaixo).
  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) calendarRef.current?.getApi().gotoDate(dataParam);
    // Link de lembrete/push (?evento=): o evento pode ser de uma empresa escondida pelo filtro salvo. Mostra todas as
    // empresas nesta visita (sem gravar no navegador), para o evento carregar e abrir.
    if (searchParams.get('evento') && empresasSalvas) setEmpresasSalvas(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const eventoParam = searchParams.get('evento');
    if (!eventoParam || eventosFiltrados.length === 0) return;
    const alvo = eventosFiltrados.find((e) => e.id === eventoParam);
    if (!alvo) return;
    abrirEvento(alvo.extendedProps.evento, true);
    const next = new URLSearchParams(searchParams);
    next.delete('data');
    next.delete('evento');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventosFiltrados]);

  // Trata o retorno do fluxo OAuth do Google Calendar (?google=conectado|erro na URL)
  useEffect(() => {
    const googleParam = searchParams.get('google');
    if (!googleParam) return;

    if (googleParam === 'conectado') {
      toast.success('Conectado ao Google Calendar!');
      setGoogleDialogOpen(true);
      carregarGoogleStatus();
    } else if (googleParam === 'erro') {
      toast.error('Não foi possível conectar ao Google Calendar. Tente novamente.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('google');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O OAuth do Google abre em uma aba nova — se o usuário concluir o consentimento lá e
  // voltar para esta aba com o diálogo ainda aberto, atualiza o status sozinho em vez de
  // deixar "Conecte sua conta Google..." parado até o usuário fechar e reabrir o diálogo.
  useEffect(() => {
    if (!googleDialogOpen) return;
    const onFocus = () => carregarGoogleStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [googleDialogOpen, carregarGoogleStatus]);

  const handleExport = async (escopo: 'visivel' | number) => {
    if (idsAtivosVisiveis.length === 0) {
      toast.error('Selecione ao menos um calendário');
      return;
    }
    setExporting(true);
    try {
      let start: string | undefined;
      let end: string | undefined;
      let subtitulo: string;

      if (escopo === 'visivel') {
        start = currentRangeRef.current.start;
        end = currentRangeRef.current.end;
        subtitulo = 'Período visível na agenda';
      } else {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const fim = new Date(hoje);
        fim.setDate(fim.getDate() + escopo * 7);
        start = hoje.toISOString();
        end = fim.toISOString();
        subtitulo = `Próximas ${escopo} semana(s) — a partir de ${hoje.toLocaleDateString('pt-BR')}`;
      }

      if (exportFormato === 'ics') {
        const blob = await calendarioEventoService.exportIcs({
          start,
          end,
          calendarioIds: idsAtivosVisiveis,
          ...(isSuperAdmin && effectiveEmpresaId ? { empresa_id: effectiveEmpresaId } : {}),
          ...paramsEscopo,
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'agenda-kora.ics';
        link.click();
        URL.revokeObjectURL(url);
        toast.success('Arquivo .ics exportado!');
        setExportDialogOpen(false);
        return;
      }

      const res = await calendarioEventoService.getEventos({
        start,
        end,
        calendarioIds: idsAtivosVisiveis,
        ...(isSuperAdmin && effectiveEmpresaId ? { empresa_id: effectiveEmpresaId } : {}),
        ...paramsEscopo,
      });
      if (!res.success) {
        toast.error('Erro ao carregar eventos para exportação');
        return;
      }
      if (res.data.length === 0) {
        toast.error('Nenhum evento no período selecionado');
        return;
      }

      await exportAgendaPdf(res.data, { titulo: 'Relatório de Agenda', subtitulo, empresaNome: multiEmpresa ? nomesEmpresasFiltro : empresaAtiva?.nome });
      toast.success('PDF exportado!');
      setExportDialogOpen(false);
    } catch {
      toast.error(exportFormato === 'ics' ? 'Erro ao exportar .ics' : 'Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleSavedEvento = () =>
    carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
  const handleSavedCalendario = async () => {
    await carregarCalendarios();
    carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
  };

  const closeQuickSeriesDialog = (shouldRevert: boolean) => {
    if (shouldRevert) {
      pendingQuickRevertRef.current?.();
    }
    pendingQuickRevertRef.current = null;
    setPendingQuickSeriesAction(null);
    setQuickSeriesDialogOpen(false);
  };

  const handleQuickSeriesScope = async (scope: 'single' | 'series' | 'future') => {
    if (!pendingQuickSeriesAction || quickSeriesLoading) return;

    const { evento, data_inicio, data_fim, tipo } = pendingQuickSeriesAction;
    setQuickSeriesLoading(true);
    try {
      const res = await calendarioEventoService.update(
        evento.calendario_id,
        evento.id,
        {
          data_inicio,
          data_fim,
        },
        { scope },
      );
      if (!res.success) {
        pendingQuickRevertRef.current?.();
        toast.error(tipo === 'move' ? 'Erro ao mover evento' : 'Erro ao redimensionar evento');
      } else {
        toast.success(
          scope === 'series'
            ? 'Série atualizada!'
            : scope === 'future'
              ? 'Esta e as próximas ocorrências atualizadas!'
              : 'Ocorrência atualizada!',
        );
        await carregarEventos(currentRangeRef.current.start, currentRangeRef.current.end);
      }
    } catch {
      pendingQuickRevertRef.current?.();
      toast.error(tipo === 'move' ? 'Erro ao mover evento' : 'Erro ao redimensionar evento');
    } finally {
      pendingQuickRevertRef.current = null;
      setPendingQuickSeriesAction(null);
      setQuickSeriesDialogOpen(false);
      setQuickSeriesLoading(false);
    }
  };

  const renderCalendarioItem = (cal: Calendario) => (
    <div
      key={cal.id}
      className="flex items-center justify-between group rounded-md px-1 py-1 hover:bg-muted/50"
    >
      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
        <Checkbox
          checked={ativados.has(cal.id)}
          onCheckedChange={() => toggleCalendario(cal.id)}
          className="h-4 w-4 border-2 rounded-sm data-[state=checked]:text-white"
          style={
            ativados.has(cal.id)
              ? ({ '--checkbox-bg': cal.cor, backgroundColor: cal.cor, borderColor: cal.cor } as React.CSSProperties)
              : ({ borderColor: cal.cor } as React.CSSProperties)
          }
        />
        <span
          className="text-sm truncate"
          title={cal.nome}
        >
          {cal.nome}
        </span>
      </label>
      {/* Gerenciar calendário é função de admin da empresa ATIVA: calendário de outra empresa só se edita lá. */}
      {isAdmin && (!escopoPessoa || empresaIdDoCalendario(cal) === effectiveEmpresaId) && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0"
          onClick={() => handleEditarCalendario(cal)}
          title="Editar calendário"
        >
          <Settings className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );

  const renderEventContent = (arg: EventContentArg) => {
    if (arg.event.extendedProps.isFeriado) {
      return (
        <div className="calendar-event-content">
          <div className="calendar-event-row">
            <PartyPopper className="calendar-event-type-icon" size={10} />
            <span className="calendar-event-title">{arg.event.title}</span>
          </div>
        </div>
      );
    }

    if (arg.event.extendedProps.isVirtual) {
      const virtual = arg.event.extendedProps.virtual as CalendarioItemVirtual;
      const Icon = virtual.tipo === 'projeto' ? Briefcase : ListChecks;
      const empresaNomeVirtual = arg.event.extendedProps.empresaNome as string | undefined;
      return (
        <div className="calendar-event-content">
          <div className="calendar-event-row">
            <Icon className="calendar-event-type-icon" size={10} />
            <span className="calendar-event-title">{arg.event.title}</span>
            {empresaNomeVirtual && (
              <span className="calendar-event-empresa" title={empresaNomeVirtual}>{empresaNomeVirtual}</span>
            )}
          </div>
        </div>
      );
    }

    const evento = arg.event.extendedProps.evento as CalendarioEvento;
    const empresaNomeEvento = arg.event.extendedProps.empresaNome as string | undefined;
    const isEntrega = calendarios.find(c => c.id === evento.calendario_id)?.tipo === 'entregas';
    const isTimeGrid = arg.view.type.startsWith('timeGrid');
    const isDetached = evento.recorrencia_excecao_tipo === 'detached';
    const isCancelled = evento.recorrencia_excecao_tipo === 'cancelled';

    const subtitulo = isTimeGrid
      ? isEntrega
        ? (evento.fornecedor || null)
        : (evento.local || null)
      : null;

    return (
      <div className="calendar-event-content">
        <div className="calendar-event-row">
          {isEntrega && <Package className="calendar-event-type-icon" size={10} />}
          <span className="calendar-event-title">{arg.event.title}</span>
          {evento.recorrencia_grupo_id && !isDetached && !isCancelled && (
            <RotateCcw className="calendar-event-recur-icon" size={8} />
          )}
          {isDetached && (
            <span className="calendar-event-series-badge calendar-event-series-badge--detached">✎</span>
          )}
          {isCancelled && (
            <span className="calendar-event-series-badge calendar-event-series-badge--cancelled">✕</span>
          )}
          {empresaNomeEvento && (
            <span className="calendar-event-empresa" title={empresaNomeEvento}>{empresaNomeEvento}</span>
          )}
        </div>
        {subtitulo && (
          <div className="calendar-event-sub">{subtitulo}</div>
        )}
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-64px)] gap-3 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            'relative flex flex-col rounded-lg border bg-card overflow-y-auto flex-shrink-0',
            !sidebarResizing && 'transition-all duration-200',
          )}
          style={{ width: sidebarOpen ? sidebarWidth : 40 }}
        >
          {/* Alça de redimensionar (arrastar) — só faz sentido com a sidebar aberta */}
          {sidebarOpen && (
            <div
              onMouseDown={iniciarResizeSidebar}
              className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-primary/30"
              title="Arrastar para redimensionar"
            />
          )}
          {/* Header da sidebar com toggle */}
          <div className={cn('flex items-center border-b p-2', sidebarOpen ? 'justify-between' : 'justify-center')}>
            {sidebarOpen && (
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pl-1">
                Calendários
              </span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={() => setSidebarOpen(v => {
                const next = !v;
                try { localStorage.setItem('calendario:sidebarOpen', String(next)); } catch { /* ignore */ }
                return next;
              })}
              title={sidebarOpen ? 'Recolher' : 'Expandir'}
            >
              {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </Button>
          </div>

          {sidebarOpen && (
            <div className="flex flex-col gap-4 p-3 pt-3">
              {/* Mini-calendário para navegação rápida */}
              <MiniCalendar
                mode="single"
                month={miniCalendarMonth}
                onMonthChange={setMiniCalendarMonth}
                onSelect={(date) => {
                  if (!date) return;
                  calendarRef.current?.getApi().gotoDate(date);
                }}
                className="mx-auto w-full p-0"
                classNames={{
                  months: 'flex flex-col',
                  month: 'space-y-2 w-full',
                  table: 'w-full border-collapse',
                  head_row: 'flex w-full justify-between',
                  head_cell: 'text-muted-foreground rounded-md w-7 font-normal text-[0.7rem]',
                  row: 'flex w-full mt-1 justify-between',
                  cell: 'h-7 w-7 text-center text-xs p-0 relative [&:has([aria-selected])]:bg-accent focus-within:relative focus-within:z-20',
                  day: 'h-7 w-7 p-0 font-normal text-xs rounded-md hover:bg-accent aria-selected:opacity-100',
                  caption: 'flex justify-center pt-1 relative items-center',
                  caption_label: 'text-xs font-medium',
                  nav_button: 'h-6 w-6 bg-transparent p-0 opacity-50 hover:opacity-100',
                }}
              />

              <Separator />

              {/* Botão novo evento */}
              <Button
                onClick={handleNovoEvento}
                className="w-full gap-2"
                disabled={!canCreateEvent}
              >
                <Plus className="h-4 w-4" />
                Novo evento
              </Button>

              <Separator />

              {/* Lista de calendários */}
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Meus calendários
                  </span>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={handleNovoCalendario}
                      title="Novo calendário"
                      disabled={isSuperAdmin && !effectiveEmpresaId}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>

                {isLoading ? (
                  <p className="text-xs text-muted-foreground px-1">Carregando...</p>
                ) : isSuperAdmin && !effectiveEmpresaId ? (
                  <p className="text-xs text-muted-foreground px-1">Selecione uma empresa.</p>
                ) : calendarios.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1">
                    {isAdmin ? 'Crie seu primeiro calendário.' : 'Nenhum calendário disponível.'}
                  </p>
                ) : (
                  multiEmpresa ? (
                    calendariosVisiveis.length === 0 ? (
                      <p className="text-xs text-muted-foreground px-1">Nenhum calendário nas empresas selecionadas.</p>
                    ) : (
                      gruposCalendarios.map((grupo) => (
                        <div key={grupo.empresaId} className="space-y-0.5 pb-1">
                          <div
                            className="truncate px-1 pt-1 text-[11px] font-medium text-muted-foreground"
                            title={grupo.empresaNome}
                          >
                            {grupo.empresaNome}
                          </div>
                          {grupo.calendarios.map(renderCalendarioItem)}
                        </div>
                      ))
                    )
                  ) : (
                    calendariosVisiveis.map(renderCalendarioItem)
                  )
                )}
              </div>

              <Separator />

              {/* Projetos e atividades do ProjetoT (virtuais, vindos do backend) — cada atividade
                  agrupada abaixo do respectivo projeto, separado dos Calendários reais e dos
                  Feriados abaixo. Checkbox do cabeçalho liga/desliga tudo de uma vez. */}
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Projetos
                  </span>
                  <Checkbox
                    checked={
                      itensVirtuaisProjetoT.length === 0
                        ? false
                        : itensVirtuaisProjetoT.every((i) => itensProjetoTAtivados.has(i.id))
                          ? true
                          : itensVirtuaisProjetoT.some((i) => itensProjetoTAtivados.has(i.id))
                            ? 'indeterminate'
                            : false
                    }
                    onCheckedChange={alternarTodosItensProjetoT}
                    disabled={itensVirtuaisProjetoT.length === 0}
                    className="h-4 w-4 border-2 rounded-sm"
                  />
                </div>

                {gruposProjetoT.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1">Nenhum projeto ou atividade com data.</p>
                ) : (
                  gruposProjetoT.map((grupo) => (
                    <div key={grupo.projetoItem?.id || `orfao-${grupo.projetoTitulo}`}>
                      {grupo.projetoItem && (
                        <ItemProjetoTRow
                          item={grupo.projetoItem}
                          ativo={itensProjetoTAtivados.has(grupo.projetoItem.id)}
                          onToggle={() => toggleItemProjetoT(grupo.projetoItem!.id)}
                          cor={coresItensProjetoT[grupo.projetoItem.id] || corPadraoItemVirtual(grupo.projetoItem)}
                          onChangeCor={(cor) => setCorItemProjetoT(grupo.projetoItem!.id, cor)}
                        />
                      )}
                      {grupo.atividades.length > 0 && (
                        <div className="ml-4 space-y-0.5 border-l pl-2">
                          {grupo.atividades.map((atividade) => (
                            <ItemProjetoTRow
                              key={atividade.id}
                              item={atividade}
                              ativo={itensProjetoTAtivados.has(atividade.id)}
                              onToggle={() => toggleItemProjetoT(atividade.id)}
                              cor={coresItensProjetoT[atividade.id] || corPadraoItemVirtual(atividade)}
                              onChangeCor={(cor) => setCorItemProjetoT(atividade.id, cor)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <Separator />

              {/* Feriados nacionais (virtuais, calculados no cliente) */}
              <label className="flex items-center gap-2 cursor-pointer rounded-md px-1 py-1 hover:bg-muted/50">
                <Checkbox
                  checked={feriadosAtivo}
                  onCheckedChange={(checked) => {
                    const next = checked === true;
                    setFeriadosAtivo(next);
                    try { localStorage.setItem('calendario:feriadosAtivo', String(next)); } catch { /* ignore */ }
                  }}
                  className="h-4 w-4 border-2 rounded-sm"
                />
                <span className="text-sm truncate">Feriados nacionais</span>
              </label>
            </div>
          )}
        </aside>

        {/* Calendário FullCalendar */}
        <main className="flex-1 min-w-0 overflow-auto">
          {isSuperAdmin && !effectiveEmpresaId ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              <h2 className="text-lg font-semibold">Selecione uma empresa</h2>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Calendário é por empresa — escolha uma empresa específica no filtro do topo (está em "Todas as empresas").
              </p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col gap-3">
              {multiEmpresa && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2" role="group" aria-label="Filtro de empresas">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Empresas</span>
                  <Button
                    type="button"
                    size="sm"
                    variant={filtroEmpresaAtivo ? 'outline' : 'default'}
                    className="h-7 rounded-full px-3 text-xs"
                    aria-pressed={!filtroEmpresaAtivo}
                    onClick={() => alterarFiltroEmpresas([])}
                  >
                    Todas
                  </Button>
                  {empresasAgenda.map((empresa) => {
                    const selecionada = filtroEmpresaAtivo && empresasFiltroIds.includes(empresa.id);
                    return (
                      <Button
                        key={empresa.id}
                        type="button"
                        size="sm"
                        variant={selecionada ? 'default' : 'outline'}
                        className="h-7 max-w-[14rem] rounded-full px-3 text-xs"
                        aria-pressed={selecionada}
                        title={empresa.nome}
                        onClick={() => toggleEmpresaFiltro(empresa.id)}
                      >
                        <span className="truncate">{empresa.nome}</span>
                      </Button>
                    );
                  })}
                </div>
              )}
              <div className="grid gap-3 rounded-lg border bg-card p-3 md:grid-cols-[minmax(0,1.6fr)_220px_220px_220px]">
                <Input
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                  placeholder="Buscar por título, local, categoria ou descrição"
                />
                <Select value={statusFiltro} onValueChange={(value) => setStatusFiltro(value as typeof statusFiltro)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={prioridadeFiltro} onValueChange={(value) => setPrioridadeFiltro(value as typeof prioridadeFiltro)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Prioridade" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORIDADE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
                  <SelectTrigger>
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas as categorias</SelectItem>
                    {categoriasDisponiveis.map((categoria) => (
                      <SelectItem key={categoria} value={categoria}>
                        {categoria}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border bg-card p-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{eventosVisiveis.length} evento(s) visível(is)</span>
                {statusFiltro !== 'todos' && <span>Status: {STATUS_LABELS[statusFiltro]}</span>}
                {prioridadeFiltro !== 'todas' && <span>Prioridade: {PRIORIDADE_LABELS[prioridadeFiltro]}</span>}
                {categoriaFiltro !== 'todas' && <span>Categoria: {categoriaFiltro}</span>}
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={handleOpenGoogleDialog}
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Google Calendar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={handleOpenAssinatura}
                  >
                    <Rss className="h-3.5 w-3.5" />
                    Assinar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => setExportDialogOpen(true)}
                    disabled={ativados.size === 0}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Exportar
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className={cn('flex items-center gap-2', modoExibicao === 'timeline' && 'invisible')}>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => calendarRef.current?.getApi().prev()}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => calendarRef.current?.getApi().today()}>
                    Hoje
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => calendarRef.current?.getApi().next()}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <span className="ml-1 text-sm font-medium capitalize">{fcTitle}</span>
                </div>

                <div className="flex items-center gap-3">
                  {/* Escala de tempo: zoom da grade (FullCalendar) OU da Linha do tempo, conforme o modo ativo */}
                  <div className="flex overflow-hidden rounded-md border">
                    {([
                      ['multiMonthYear', 'Ano'],
                      ['dayGridMonth', 'Mês'],
                      ['timeGridWeek', 'Semana'],
                      ['timeGridDay', 'Dia'],
                    ] as const).map(([view, label]) => (
                      <Button
                        key={view}
                        variant={
                          (modoExibicao === 'grade' && fcViewType === view) ||
                          (modoExibicao === 'timeline' && escalaTimeline === VIEW_PARA_ESCALA_TIMELINE[view])
                            ? 'default'
                            : 'ghost'
                        }
                        size="sm"
                        className="h-8 rounded-none px-3 text-xs"
                        onClick={() => {
                          if (modoExibicao === 'timeline') {
                            setEscalaTimeline(VIEW_PARA_ESCALA_TIMELINE[view]);
                          } else {
                            setModoExibicao('grade');
                            calendarRef.current?.getApi().changeView(view);
                          }
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>

                  {/* Modo de visualização: forma diferente de renderizar os mesmos eventos */}
                  <div className="flex overflow-hidden rounded-md border">
                    <Button
                      variant={modoExibicao === 'grade' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 gap-1.5 rounded-none px-3 text-xs"
                      onClick={() => {
                        setModoExibicao('grade');
                        if (fcViewType === 'listWeek') calendarRef.current?.getApi().changeView('dayGridMonth');
                      }}
                    >
                      <CalendarDays className="h-3.5 w-3.5" /> Calendário
                    </Button>
                    <Button
                      variant={modoExibicao === 'lista' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 gap-1.5 rounded-none px-3 text-xs"
                      onClick={() => {
                        setModoExibicao('lista');
                        calendarRef.current?.getApi().changeView('listWeek');
                      }}
                    >
                      <ListIcon className="h-3.5 w-3.5" /> Lista
                    </Button>
                    <Button
                      variant={modoExibicao === 'timeline' ? 'default' : 'ghost'}
                      size="sm"
                      className="h-8 gap-1.5 rounded-none px-3 text-xs"
                      onClick={() => setModoExibicao('timeline')}
                    >
                      <GanttChart className="h-3.5 w-3.5" /> Linha do tempo
                    </Button>
                  </div>

                  {/* Altura da célula do dia: mais espaço = mais eventos empilhados visíveis antes
                      do "+N mais" (só faz sentido na grade mensal/anual, que usa dia em bloco). */}
                  {modoExibicao === 'grade' && (fcViewType === 'dayGridMonth' || fcViewType === 'multiMonthYear') && (
                    <div className="flex items-center overflow-hidden rounded-md border">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-none"
                        title="Células menores (menos eventos visíveis)"
                        disabled={alturaGrade === ALTURAS_GRADE[0]}
                        onClick={() => {
                          const idx = ALTURAS_GRADE.indexOf(alturaGrade);
                          const next = ALTURAS_GRADE[Math.max(0, idx - 1)];
                          setAlturaGrade(next);
                          try { localStorage.setItem('calendario:alturaGrade', next); } catch { /* ignore */ }
                        }}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </Button>
                      <span className="w-24 select-none px-1 text-center text-xs text-muted-foreground">
                        {ALTURA_GRADE_LABEL[alturaGrade]}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-none"
                        title="Células maiores (mais eventos visíveis)"
                        disabled={alturaGrade === ALTURAS_GRADE[ALTURAS_GRADE.length - 1]}
                        onClick={() => {
                          const idx = ALTURAS_GRADE.indexOf(alturaGrade);
                          const next = ALTURAS_GRADE[Math.min(ALTURAS_GRADE.length - 1, idx + 1)];
                          setAlturaGrade(next);
                          try { localStorage.setItem('calendario:alturaGrade', next); } catch { /* ignore */ }
                        }}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {modoExibicao === 'timeline' ? (
                <div className="min-h-0 flex-1 overflow-hidden">
                  <CalendarioTimeline
                    eventos={[...eventosVisiveis, ...feriadoEvents, ...projetoEvents, ...atividadeEvents]}
                    onEventoClick={abrirEvento}
                    onVirtualClick={abrirPreviewVirtual}
                    granularidade={escalaTimeline}
                  />
                </div>
              ) : (
              <div className={cn('min-h-0 flex-1 rounded-lg border bg-card p-2 overflow-hidden', `calendario-altura-${alturaGrade}`)}>
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, listPlugin, multiMonthPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  locale={ptBrLocale}
                  headerToolbar={false}
                  views={{
                    multiMonthYear: { multiMonthMaxColumns: 3, duration: { years: 1 } },
                  }}
                  buttonText={{
                    today: 'Hoje',
                    month: 'Mês',
                    week: 'Semana',
                    day: 'Dia',
                    list: 'Lista',
                    multiMonthYear: 'Ano',
                  }}
                  events={[...eventosVisiveis, ...feriadoEvents, ...projetoEvents, ...atividadeEvents]}
                  selectable={canCreateEvent}
                  selectMirror
                  editable={calendariosEditaveis.length > 0}
                  dayMaxEvents
                  weekends
                  nowIndicator
                  height="100%"
                  slotMinTime="06:00:00"
                  slotMaxTime="23:00:00"
                  allDayText="Dia inteiro"
                  noEventsText="Nenhum evento neste período"
                  moreLinkText={n => `+${n} eventos`}
                  dateClick={handleDateClick}
                  select={handleSelect}
                  eventClick={handleEventClick}
                  eventDrop={handleEventDrop}
                  eventResize={handleEventResize}
                  eventContent={renderEventContent}
                  datesSet={handleDatesSet}
                />
              </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Modais */}
      <ItemVirtualPreviewDialog
        item={virtualPreviewItem}
        open={virtualPreviewOpen}
        onOpenChange={setVirtualPreviewOpen}
      />

      <EventoModal
        open={eventoModalOpen}
        onOpenChange={setEventoModalOpen}
        evento={eventoSelecionado}
        calendarios={calendarios}
        defaultStart={defaultStart}
        defaultEnd={defaultEnd}
        defaultCalendarioId={defaultCalendarioId}
        usuarios={usuarios}
        usuariosEmpresaId={effectiveEmpresaId || undefined}
        empresasAdminIds={empresasAdminIds}
        onSaved={handleSavedEvento}
        isAdmin={isAdmin}
        forceReadOnly={eventoSomenteLeitura}
      />

      <CalendarioModal
        open={calendarioModalOpen}
        onOpenChange={setCalendarioModalOpen}
        calendario={calendarioSelecionado}
        usuarios={usuarios}
        onSaved={handleSavedCalendario}
        empresaId={isSuperAdmin ? effectiveEmpresaId : undefined}
      />

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar agenda</DialogTitle>
            <DialogDescription>
              Escolha o formato e o período a exportar. No PDF, as colunas de entrega são incluídas automaticamente quando houver eventos de calendários de entregas. O arquivo .ics pode ser importado no Google Calendar, Apple Calendar ou Outlook.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">Formato</span>
            <Select value={exportFormato} onValueChange={(value) => setExportFormato(value as 'pdf' | 'ics')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pdf">PDF (relatório)</SelectItem>
                <SelectItem value="ics">iCalendar (.ics)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Button variant="outline" className="justify-start" onClick={() => handleExport('visivel')} disabled={exporting}>
              {exporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Período visível na agenda
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => handleExport(1)} disabled={exporting}>
              Próxima 1 semana
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => handleExport(2)} disabled={exporting}>
              Próximas 2 semanas
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => handleExport(4)} disabled={exporting}>
              Próximas 4 semanas
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={assinaturaDialogOpen} onOpenChange={setAssinaturaDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Assinar sua agenda</DialogTitle>
            <DialogDescription>
              Adicione esta URL no Google Calendar, Apple Calendar ou Outlook para ver seus eventos
              automaticamente (a atualização entre apps pode levar algumas horas). A lista de eventos
              respeita os calendários aos quais você tem acesso.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input value={assinaturaLoading ? 'Carregando...' : assinaturaUrl} readOnly className="font-mono text-xs" />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleCopiarAssinatura}
              disabled={assinaturaLoading || !assinaturaUrl}
              title="Copiar URL"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground"
              onClick={handleRegenerarAssinatura}
              disabled={assinaturaLoading}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Gerar nova URL (a atual deixa de funcionar)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={googleDialogOpen} onOpenChange={setGoogleDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Google Calendar</DialogTitle>
            <DialogDescription>
              Sincronize os calendários selecionados com um calendário dedicado ("Gestão Rmbs") na sua
              conta Google. A sincronização acontece nos dois sentidos por polling periódico.
            </DialogDescription>
          </DialogHeader>

          {googleLoading && !googleStatus ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : !googleStatus?.configurado ? (
            <p className="text-sm text-muted-foreground">
              A integração com o Google Calendar ainda não foi configurada pelo administrador do sistema.
            </p>
          ) : !googleStatus.conectado ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-muted-foreground">
                Conecte sua conta Google para sincronizar eventos automaticamente.
              </p>
              <Button type="button" className="gap-2" onClick={handleConectarGoogle} disabled={googleLoading}>
                {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                Conectar com o Google
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="text-sm">
                Conectado como <span className="font-medium">{googleStatus.google_email}</span>
                <div className="text-xs text-muted-foreground">
                  {googleStatus.ultima_push_em && `Último envio: ${new Date(googleStatus.ultima_push_em).toLocaleString('pt-BR')}`}
                  {googleStatus.ultima_push_em && googleStatus.ultima_pull_em && ' · '}
                  {googleStatus.ultima_pull_em && `Última recepção: ${new Date(googleStatus.ultima_pull_em).toLocaleString('pt-BR')}`}
                  {!googleStatus.ultima_push_em && !googleStatus.ultima_pull_em && 'Ainda não sincronizado'}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">Calendários sincronizados</span>
                <div className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-md border p-2">
                  {calendarios.length === 0 && (
                    <span className="text-xs text-muted-foreground">Nenhum calendário disponível</span>
                  )}
                  {/* Agenda única: calendários de TODAS as empresas (não só os do filtro da tela), agrupados. */}
                  {gruposCalendariosGoogle.map((grupo) => (
                    <div key={grupo.empresaId || 'unica'} className="flex flex-col gap-2">
                      {multiEmpresa && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {grupo.empresaNome}
                        </span>
                      )}
                      {grupo.calendarios.map((cal) => (
                        <label key={cal.id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={googleSelecionados.has(cal.id)}
                            onCheckedChange={(checked) => handleToggleGoogleCalendario(cal.id, checked === true)}
                          />
                          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: cal.cor }} />
                          {cal.nome}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={handleSalvarGoogleCalendarios}
                  disabled={googleLoading}
                >
                  Salvar seleção
                </Button>
              </div>

              <DialogFooter className="sm:justify-between">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-destructive"
                  onClick={handleDesconectarGoogle}
                  disabled={googleLoading}
                >
                  <Unlink className="h-3.5 w-3.5" />
                  Desconectar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={handleSincronizarGoogleAgora}
                  disabled={googleSyncing}
                >
                  <RefreshCw className={cn('h-3.5 w-3.5', googleSyncing && 'animate-spin')} />
                  Sincronizar agora
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={quickSeriesDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !quickSeriesLoading) {
            closeQuickSeriesDialog(true);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingQuickSeriesAction?.tipo === 'move' ? 'Mover ocorrência ou série?' : 'Redimensionar ocorrência ou série?'}
            </DialogTitle>
            <DialogDescription>
              Esta ação foi feita direto no calendário e o evento pertence a uma série recorrente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch sm:justify-start">
            <Button type="button" variant="outline" disabled={quickSeriesLoading} onClick={() => handleQuickSeriesScope('single')}>
              Somente esta ocorrência
            </Button>
            <Button type="button" variant="outline" disabled={quickSeriesLoading} onClick={() => handleQuickSeriesScope('future')}>
              Esta e as futuras
            </Button>
            <Button type="button" disabled={quickSeriesLoading} onClick={() => handleQuickSeriesScope('series')}>
              {quickSeriesLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Aplicar na série inteira
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GuiaModuloFlutuante imagemSrc="/guias-modulos/calendario.png" titulo="Calendário" />
    </AppLayout>
  );
}
